import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { organisations, type OrgUserRow, type UserSpaceRow } from "@/api/client";
import { ActionRow } from "@/components/ActionRow";
import { Chip } from "@/components/Chip";
import { Drawer } from "@/components/Drawer";
import { ErrorNote } from "@/components/ErrorNote";
import { Icon } from "@/components/icons";
import { KV } from "@/components/KV";
import { SearchInput, Select } from "@/components/inputs";
import { ROLE_LABEL, orgQk } from "@/modules/organisations/data";

/** One person, and everything an admin may do to them.
 *
 *  Dattatray listed what was missing — *"reset MFA, send password reset link,
 *  send UDS tool link (whichever tool is applicable to that company and hence
 *  the user!)"* — and then, shown the rest of the gap, *"implement/port all in
 *  orbit."* Orbit could create a person and list people. Nothing else.
 *
 *  A DRAWER, because it is detail over a list (the module UI grammar) and
 *  because every action here needs the row's context to be safe: what the
 *  refusals say depends on this person's role, their organisation and whether
 *  they have ever signed in.
 *
 *  THE REFUSALS ARE THE FEATURE. Four of these actions can lock somebody out
 *  permanently, so the server refuses any that would leave the platform or an
 *  organisation with no active admin, and this shows the sentence it gives
 *  back rather than a generic failure. That is why nothing here is disabled
 *  optimistically: the console does not re-derive the floor, it asks.
 *
 *  NO PASSWORD IS EVER SHOWN (SEC-05). "Send a reset link" emails the person a
 *  link to set their own; there is no version of this screen that reveals a
 *  credential, because none is created.
 *
 *  THE TOOL LIST IS DERIVED, never picked. `userTools` answers which tools this
 *  person may be sent, from what their organisation holds, which spaces they
 *  may enter, and which releases are published — so an empty list is an answer
 *  and it comes with the reason.
 */
export function PersonDrawer({ user, onClose }: {
  user: OrgUserRow; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [editEmail, setEditEmail] = useState(false);
  const [email, setEmail] = useState(user.login_id);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: orgQk.list });
    if (user.organisation_id) {
      void qc.invalidateQueries({ queryKey: orgQk.detail(user.organisation_id) });
    }
    void qc.invalidateQueries({ queryKey: ["organisations", "users"] });
  };

  const spaces = useQuery({
    queryKey: ["organisations", "users", user.user_id, "spaces"],
    queryFn: () => organisations.userSpaces(user.user_id),
    staleTime: 30_000, refetchOnWindowFocus: false,
  });
  const tools = useQuery({
    queryKey: ["organisations", "users", user.user_id, "tools"],
    queryFn: () => organisations.userTools(user.user_id),
    staleTime: 30_000, refetchOnWindowFocus: false,
  });

  const [sent, setSent] = useState("");

  const reset = useMutation({
    mutationFn: () => organisations.sendPasswordReset(user.user_id),
    onSuccess: (r) => {
      setSent(r.invite
        ? `Invite sent to ${r.to} — they set their own password from it.`
        : `Reset link sent to ${r.to}.`);
      refresh();
    },
  });
  const mfa = useMutation({
    mutationFn: () => organisations.resetMfa(user.user_id),
    onSuccess: () => { setSent("Two-factor cleared. They will enrol again at next sign-in."); refresh(); },
  });
  const role = useMutation({
    mutationFn: (v: string) => organisations.setUserRole(user.user_id, v),
    onSuccess: () => { setSent("Role changed."); refresh(); },
  });
  const status = useMutation({
    mutationFn: (v: string) => organisations.setUserStatus(user.user_id, v),
    onSuccess: (r) => { setSent(`Now ${r.status}.`); refresh(); },
  });
  const addr = useMutation({
    mutationFn: () => organisations.setUserEmail(user.user_id, email.trim()),
    onSuccess: (r) => {
      setEditEmail(false);
      setSent(`Address changed to ${r.login_id} — a verification link is on its way.`);
      refresh();
    },
  });
  const drop = useMutation({
    mutationFn: () => organisations.deleteUser(user.user_id),
    onSuccess: () => { refresh(); onClose(); },
  });
  const tool = useMutation({
    mutationFn: (kind: string) => organisations.sendToolLink(user.user_id, kind),
    onSuccess: (r) => { setSent(`${r.product} ${r.tool} ${r.version} sent to ${r.to}.`); },
  });

  const grants = useMutation({
    mutationFn: (ids: string[]) => organisations.setUserSpaces(user.user_id, ids),
    onSuccess: () => {
      setSent("Space access updated.");
      void qc.invalidateQueries({
        queryKey: ["organisations", "users", user.user_id, "spaces"] });
    },
  });

  const err = reset.error ?? mfa.error ?? role.error ?? status.error
    ?? addr.error ?? drop.error ?? tool.error ?? grants.error;
  const busy = reset.isPending || mfa.isPending || role.isPending
    || status.isPending || addr.isPending || drop.isPending || tool.isPending
    || grants.isPending;

  const rows: UserSpaceRow[] = spaces.data?.spaces ?? [];
  const granted = rows.filter((r) => r.granted).map((r) => r.cps_id);

  return (
    <Drawer onClose={onClose} width="min(640px,96vw)" header={
      <span style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
        <span style={{ fontWeight: 700 }}>{user.name}</span>
        <span className="mono cap">{user.login_id}</span>
      </span>
    }>
      <div className="section-label flush">Account</div>
      <div className="panel panel-pad">
        <KV k="Role" v={ROLE_LABEL[user.role] ?? user.role} mono={false} />
        <KV k="Organisation" v={user.organisation_id ?? "— platform operator —"} />
        <KV k="Status" v={user.status} mono={false} />
        <KV k="Two-factor" v={user.mfa ? "enrolled" : "not set up"} mono={false} />
      </div>

      {/* WHAT CHANGED, in words. Every action here is invisible from the
          console once it has happened — an email left, a flag flipped — so
          saying so is the only feedback there is. */}
      {sent && <div className="panel panel-pad cap" style={{
        marginTop: 10, borderColor: "var(--ok)",
        background: "color-mix(in srgb, var(--ok) 8%, transparent)",
      }}>{sent}</div>}
      <ErrorNote panel error={err} />

      <div className="section-label">Sign-in</div>
      <div className="panel panel-pad">
        {editEmail ? (
          <ActionRow top={0}>
            <SearchInput value={email} autoFocus style={{ minWidth: 260 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} />
            <button className="btn ghost" onClick={() => {
              setEditEmail(false); setEmail(user.login_id);
            }}>Cancel</button>
            <button className="btn teal" disabled={busy || !email.includes("@")}
              onClick={() => addr.mutate()}>Save address</button>
          </ActionRow>
        ) : (
          <ActionRow top={0}>
            <span className="cap">
              {user.email_verified
                ? "Address verified."
                : "Address not verified — they cannot receive a tool link yet."}
            </span>
            <button className="btn ghost" disabled={busy}
              onClick={() => setEditEmail(true)}>
              <Icon name="edit" /> Change address
            </button>
            <button className="btn ghost" disabled={busy || !user.mfa}
              title={user.mfa ? "Clear their second factor and every backup code"
                : "They have not set up two-factor"}
              onClick={() => mfa.mutate()}>
              <Icon name="restore" /> Reset two-factor
            </button>
            <button className="btn teal" disabled={busy}
              onClick={() => reset.mutate()}>
              Send {user.email_verified ? "reset link" : "invite"}
            </button>
          </ActionRow>
        )}
      </div>

      <div className="section-label">Role and status</div>
      <div className="panel panel-pad">
        <ActionRow top={0}>
          {/* NOT DISABLED OPTIMISTICALLY. The console does not re-derive the
              admin floor — it asks, and shows the sentence the server gives
              back. A locally-computed guess would be wrong the moment two
              people edit at once, and wrong in the direction that hides a
              real refusal. */}
          <Select value={user.role} disabled={busy}
            onChange={(e) => role.mutate(e.target.value)} style={{ minWidth: 170 }}>
            {Object.entries(ROLE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
          <Select value={user.status} disabled={busy}
            onChange={(e) => status.mutate(e.target.value)} style={{ minWidth: 150 }}>
            <option value="active">active</option>
            <option value="suspended">suspended</option>
            <option value="deactivated">deactivated</option>
          </Select>
        </ActionRow>
      </div>

      <div className="section-label">Space access</div>
      <div className="panel panel-pad">
        {rows.length === 0 ? (
          <span className="cap">
            {spaces.isLoading ? "Loading…"
              : "This organisation holds no product spaces yet, so there is nothing to grant."}
          </span>
        ) : (
          <>
            {rows.map((r) => (
              <label key={r.cps_id} style={{ display: "flex", gap: 10,
                alignItems: "center", padding: "3px 0" }}>
                <input type="checkbox" checked={r.granted} disabled={busy}
                  onChange={(e) => grants.mutate(
                    e.target.checked
                      ? [...granted, r.cps_id]
                      : granted.filter((c) => c !== r.cps_id))} />
                <span className="mono cap" style={{ minWidth: 90 }}>{r.kind}</span>
                <span>{r.space}</span>
                {!r.enabled && <Chip tone="mute">disabled</Chip>}
              </label>
            ))}
            <div className="cap" style={{ marginTop: 6, color: "var(--t-dim)" }}>
              A space their organisation holds but they cannot enter is a tool
              they cannot be sent.
            </div>
          </>
        )}
      </div>

      <div className="section-label">Client tools</div>
      <div className="panel panel-pad">
        {tools.data?.blocked_because ? (
          // THE REASON, not an empty picker. "Nothing available" sends
          // somebody to guess between four different causes.
          <span className="cap">{tools.data.blocked_because}</span>
        ) : (tools.data?.tools ?? []).length === 0 ? (
          <span className="cap">
            {tools.isLoading ? "Loading…"
              : "No tool is available to them yet — it needs the product allocated "
                + "to their organisation, access to a space under it, and a published release."}
          </span>
        ) : (
          (tools.data?.tools ?? []).map((t) => (
            <ActionRow key={`${t.kind}:${t.tool_slug}`} top={6}>
              <span style={{ fontWeight: 700 }}>{t.product}</span>
              <span className="mono cap">{t.tool_slug}</span>
              <span className="cap">{t.version_name}</span>
              <button className="btn teal" disabled={busy}
                onClick={() => tool.mutate(t.kind)}>Send link</button>
            </ActionRow>
          ))
        )}
      </div>

      <div className="section-label">Remove</div>
      <div className="panel panel-pad">
        {confirmDelete ? (
          <ActionRow top={0}>
            <span>
              Delete <b>{user.login_id}</b> for good? Deactivating keeps the
              record and is the right answer for somebody who has left.
            </span>
            <button className="btn ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button className="btn red" disabled={busy}
              onClick={() => drop.mutate()}>Delete the account</button>
          </ActionRow>
        ) : (
          <ActionRow top={0}>
            <span className="cap">
              Deleting is for an account that should not exist. To remove
              somebody who has left, set them to deactivated above.
            </span>
            <button className="btn red" disabled={busy}
              onClick={() => setConfirmDelete(true)}>
              <Icon name="delete" /> Delete
            </button>
          </ActionRow>
        )}
      </div>
    </Drawer>
  );
}
